import { FORFAIT_NAMES } from "../data/flowHome";
import { LearningNeedsPreview } from "./live/LearningNeedsPreview";
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { BASE_AVAILABILITY, slotId } from "../data/flow2Welcome";
import { FEATURES } from "../config/features";

const base = process.env.REACT_APP_BACKEND_URL || "";

const STATUS = {
  waiting: "En attente du parent",
  pending: "Invitation envoyée",
  active: "Compte actif",
  expired: "Invitation expirée",
  revoked: "Invitation révoquée",
  deactivated: "Compte désactivé",
  removed: "Retiré",
};

const WEEK_DAYS = [
  ["lun", "Lundi"],
  ["mar", "Mardi"],
  ["mer", "Mercredi"],
  ["jeu", "Jeudi"],
  ["ven", "Vendredi"],
  ["sam", "Samedi"],
  ["dim", "Dimanche"],
];

const inputClass =
  "w-full rounded-xl border border-gray-200 bg-[#FBFCFC] px-3 py-2 text-sm text-[#122118] outline-none focus:border-[#25D366]";

function withPlan(hours = {}) {
  return {
    timezone: hours.timezone || "America/Toronto",
    slotIds: hours.slotIds || [],
    exceptions: hours.exceptions || [],
    dailyPlan: hours.dailyPlan || "18:00",
    weeklyPlanDay: hours.weeklyPlanDay || "jeu",
    weeklyPlanTime: hours.weeklyPlanTime || "16:00",
    appointments: hours.appointments || [],
  };
}

async function api(path, token, options = {}) {
  const response = await fetch(`${base}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      "X-Account-Token": token,
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = typeof data.detail === "string" ? data.detail : "Action impossible.";
    throw new Error(detail);
  }
  return data;
}

const FamilySettings = ({ session, onClose, onStudentFocus }) => {
  const [students, setStudents] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [draft, setDraft] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", grade: "", school: "" });
  const [collab, setCollab] = useState({ name: "", email: "", role: "tutor", studentId: "" });
  const [schedule, setSchedule] = useState(withPlan());
  const [exception, setException] = useState({ date: "", note: "" });
  const [appointment, setAppointment] = useState({ date: "", time: "", note: "" });
  const [demoEnabled, setDemoEnabled] = useState(false);
  const [forfaits, setForfaits] = useState(null);
  const parent = session.role === "parent";
  const canEditSchedule = session.role === "parent" || session.role === "student";

  const load = () => {
    Promise.all([api("/api/family", session.token), api("/api/family/schedule", session.token)])
      .then(([data, hours]) => {
        setStudents(data.students || []);
        setCollaborators(data.collaborators || []);
        setForfaits({ own: data.ownForfait, family: data.familyForfait || 1 });
        setSchedule(withPlan(hours));
      })
      .catch((err) => setError(err.message));
  };

  useEffect(() => {
    fetch(`${base}/api/dev/status`)
      .then((response) => (response.ok ? response.json() : { enabled: false }))
      .then((data) => setDemoEnabled(Boolean(data.enabled)))
      .catch(() => setDemoEnabled(false));
    load();
    // The token identifies the account for this panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.token]);

  const run = async (action) => {
    setError("");
    setNotice("");
    try {
      const result = await action();
      if (result?.confirmPath) {
        setNotice(`Mode local : ${result.confirmPath}`);
      }
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0E1A14]/55 p-4">
      <div
        data-testid="family-settings"
        className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-2xl font-bold text-[#122118]">Réglages</h2>
            <p className="mt-1 text-sm text-[#4a5f53]">
              {FEATURES.schedule ? "Élèves, collaborateurs et horaire de ce compte." : "Élèves et collaborateurs de ce compte."}
            </p>
          </div>
          <button type="button" aria-label="Fermer" onClick={onClose} className="rounded-full p-2 text-[#122118]">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && !draft && <p className="mt-4 text-sm text-[#9b3b3b]" data-testid="settings-error">{error}</p>}
        {notice && <p className="mt-4 break-all text-sm text-[#122118]">{notice}</p>}

        {forfaits && (
          <div className="mt-5 rounded-2xl border border-gray-100 p-4" data-testid="settings-forfait">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#189346]">Forfait</p>
            {parent && (
              <p className="mt-1 text-sm text-[#122118]" data-testid="settings-forfait-parent">
                Votre forfait : <span className="font-semibold">{FORFAIT_NAMES[forfaits.own] || FORFAIT_NAMES[1]}</span>
              </p>
            )}
            {(parent ? students : [{ id: "self", name: "" }]).map((student) => (
              <p key={student.id} className="mt-1 text-sm text-[#122118]" data-testid={`settings-forfait-student-${student.id}`}>
                {parent ? `${student.name || student.email} : ` : "Ton forfait : "}
                <span className="font-semibold">{FORFAIT_NAMES[forfaits.family]}</span>
                <span className="text-[#829C8D]"> (celui de la famille)</span>
              </p>
            ))}
          </div>
        )}

        <ul className="mt-5 space-y-3">
          {students.map((student) => (
            <li key={student.id} className="rounded-2xl border border-gray-100 p-4" data-testid={`student-${student.id}`}>
              {draft?.id === student.id ? (
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    run(async () => {
                      await api(`/api/family/students/${student.id}`, session.token, {
                        method: "PATCH",
                        body: JSON.stringify(draft),
                      });
                      if (student.id === session.studentId) onStudentFocus?.(draft.name);
                      setDraft(null);
                    });
                  }}
                >
                  <input className={inputClass} value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} aria-label="Prénom" />
                  <input className={`${inputClass} mt-2`} value={draft.grade} onChange={(event) => setDraft({ ...draft, grade: event.target.value })} aria-label="Niveau" />
                  <input className={`${inputClass} mt-2`} value={draft.school} onChange={(event) => setDraft({ ...draft, school: event.target.value })} aria-label="École" />
                  <input className={`${inputClass} mt-2`} type="tel" placeholder="Son numéro (facultatif)" value={draft.phone || ""} onChange={(event) => setDraft({ ...draft, phone: event.target.value })} aria-label="Téléphone de l’élève" data-testid="student-edit-phone" />
                  <label className="mt-3 block text-xs font-semibold text-[#122118]" htmlFor={`precisions-${student.id}`}>
                    Précisions <span className="font-normal text-[#829C8D]">(optionnel)</span>
                  </label>
                  <textarea
                    id={`precisions-${student.id}`}
                    data-testid="student-edit-precisions"
                    rows={3}
                    className={`${inputClass} mt-1`}
                    placeholder={`Quels sont les principaux défis de ${draft.name || "votre enfant"} avec ses études et les difficultés que cela vous occasionne comme parent ?`}
                    value={draft.precisions || ""}
                    onChange={(event) => setDraft({ ...draft, precisions: event.target.value })}
                  />
                  {error && <p className="mt-2 text-sm text-[#9b3b3b]" data-testid="student-edit-error">{error}</p>}
                  <div className="mt-3 flex gap-2">
                    <button type="submit" data-testid="student-edit-save" className="rounded-full px-4 py-2 text-sm font-semibold" style={{ background: "var(--sp-primary)", color: "var(--sp-text-on-primary)" }}>
                      Enregistrer
                    </button>
                    <button type="button" onClick={() => setDraft(null)} className="rounded-full px-4 py-2 text-sm font-semibold text-[#122118]">
                      Annuler
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <p className="font-semibold text-[#122118]">{student.name || "Élève"}</p>
                  <p className="text-sm text-[#4a5f53]">{student.email}</p>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-[#189346]">
                    {STATUS[student.status] || student.status}
                  </p>
                  {parent && (
                    <div className="mt-3 rounded-xl bg-[#F6F8F7] px-3 py-2" data-testid={`student-precisions-${student.id}`}>
                      <p className="text-[11px] font-semibold text-[#4a5f53]">Précisions</p>
                      <p className="whitespace-pre-line text-sm text-[#122118]">
                        {student.precisions || <span className="text-[#829C8D]">Aucune précision. Touchez « Modifier » pour en ajouter.</span>}
                      </p>
                    </div>
                  )}
                  {parent && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button type="button" className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold" onClick={() => onStudentFocus?.(student.name, student.id)}>
                        Voir dans l'app
                      </button>
                      <button type="button" data-testid={`student-edit-${student.id}`} className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold" onClick={() => setDraft({ id: student.id, name: student.name, grade: student.grade, school: student.school, phone: student.phone, gender: student.gender, precisions: student.precisions || "" })}>
                        Modifier
                      </button>
                      {["pending", "expired", "waiting"].includes(student.status) && (
                        <>
                          <button type="button" data-testid="student-resend" className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold" onClick={() => run(() => api(`/api/family/students/${student.id}/resend`, session.token, { method: "POST" }))}>
                            Renvoyer
                          </button>
                          <button type="button" className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold" onClick={() => run(() => api(`/api/family/students/${student.id}/revoke`, session.token, { method: "POST" }))}>
                            Révoquer
                          </button>
                        </>
                      )}
                      {student.status === "active" && (
                        <button type="button" className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold" onClick={() => run(() => api(`/api/family/students/${student.id}/deactivate`, session.token, { method: "POST" }))}>
                          Désactiver
                        </button>
                      )}
                      {confirmRemove === student.id ? (
                        <span className="flex items-center gap-2 text-xs text-[#4a5f53]">
                          L'historique est conservé.
                          <button type="button" data-testid="student-remove-confirm" className="font-semibold text-[#9b3b3b]" onClick={() => run(async () => { await api(`/api/family/students/${student.id}/remove`, session.token, { method: "POST" }); setConfirmRemove(null); })}>
                            Retirer
                          </button>
                          <button type="button" onClick={() => setConfirmRemove(null)}>Annuler</button>
                        </span>
                      ) : (
                        <button type="button" className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold" onClick={() => setConfirmRemove(student.id)}>
                          Retirer
                        </button>
                      )}
                    </div>
                  )}
                  {parent && <LearningNeedsPreview studentId={student.id} />}
                </>
              )}
            </li>
          ))}
        </ul>

        {parent && (
          <div className="mt-5">
            {adding ? (
              <form
                className="space-y-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  run(async () => {
                    const created = await api("/api/family/students", session.token, {
                      method: "POST",
                      body: JSON.stringify(form),
                    });
                    setForm({ name: "", email: "", phone: "", grade: "", school: "" });
                    setAdding(false);
                    return created;
                  });
                }}
              >
                <input required className={inputClass} placeholder="Prénom" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
                <input required type="email" className={inputClass} placeholder="Courriel" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
                <input className={inputClass} placeholder="Téléphone" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} />
                <input className={inputClass} placeholder="Niveau" value={form.grade} onChange={(event) => setForm({ ...form, grade: event.target.value })} />
                <input className={inputClass} placeholder="École" value={form.school} onChange={(event) => setForm({ ...form, school: event.target.value })} />
                <div className="flex gap-2 pt-1">
                  <button type="submit" data-testid="student-invite" className="rounded-full px-4 py-2 text-sm font-semibold" style={{ background: "var(--sp-primary)", color: "var(--sp-text-on-primary)" }}>
                    Inviter
                  </button>
                  <button type="button" onClick={() => setAdding(false)} className="rounded-full px-4 py-2 text-sm font-semibold text-[#122118]">
                    Annuler
                  </button>
                </div>
              </form>
            ) : (
              <button type="button" data-testid="student-add" onClick={() => setAdding(true)} className="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-[#122118]">
                Inviter un élève
              </button>
            )}
          </div>
        )}

        {parent && (
          <div className="mt-8 border-t border-gray-100 pt-5">
            <h3 className="font-display text-lg font-bold text-[#122118]">Tuteurs et enseignants</h3>
            <ul className="mt-3 space-y-2">
              {collaborators.map((person) => (
                <li key={person.id} className="rounded-2xl border border-gray-100 p-3 text-sm">
                  <p className="font-semibold text-[#122118]">
                    {person.name || person.email} · {person.role === "teacher" ? "Enseignant" : "Tuteur"}
                  </p>
                  <p className="text-[#4a5f53]">{STATUS[person.status] || person.status}</p>
                  {person.status !== "revoked" && (
                    <button
                      type="button"
                      className="mt-2 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold"
                      onClick={() => run(() => api(`/api/family/collaborators/${person.id}/revoke`, session.token, { method: "POST" }))}
                    >
                      Révoquer l'accès
                    </button>
                  )}
                </li>
              ))}
            </ul>
            <form
              className="mt-3 space-y-2"
              onSubmit={(event) => {
                event.preventDefault();
                run(async () => {
                  const created = await api("/api/family/collaborators", session.token, {
                    method: "POST",
                    body: JSON.stringify(collab),
                  });
                  setCollab({ name: "", email: "", role: "tutor", studentId: collab.studentId });
                  return created;
                });
              }}
            >
              <input className={inputClass} placeholder="Nom" value={collab.name} onChange={(event) => setCollab({ ...collab, name: event.target.value })} />
              <input required type="email" className={inputClass} placeholder="Courriel" value={collab.email} onChange={(event) => setCollab({ ...collab, email: event.target.value })} />
              <select className={inputClass} value={collab.role} onChange={(event) => setCollab({ ...collab, role: event.target.value })} aria-label="Rôle">
                <option value="tutor">Tuteur</option>
                <option value="teacher">Enseignant</option>
              </select>
              <select className={inputClass} required value={collab.studentId} onChange={(event) => setCollab({ ...collab, studentId: event.target.value })} aria-label="Élève">
                <option value="">Élève concerné</option>
                {students.map((student) => (
                  <option key={student.id} value={student.id}>{student.name}</option>
                ))}
              </select>
              <button type="submit" data-testid="collab-invite" className="rounded-full px-4 py-2 text-sm font-semibold" style={{ background: "var(--sp-primary)", color: "var(--sp-text-on-primary)" }}>
                Inviter
              </button>
            </form>
          </div>
        )}

        {FEATURES.schedule && canEditSchedule && (
          <div className="mt-8 border-t border-gray-100 pt-5">
            <h3 className="font-display text-lg font-bold text-[#122118]">Horaire</h3>
            <label className="mt-3 block text-xs font-bold uppercase tracking-wider text-[#189346]" htmlFor="schedule-tz">
              Fuseau
            </label>
            <select
              id="schedule-tz"
              className={`${inputClass} mt-2`}
              value={schedule.timezone}
              onChange={(event) => setSchedule({ ...schedule, timezone: event.target.value })}
            >
              <option value="America/Toronto">America/Toronto</option>
              <option value="America/Montreal">America/Montreal</option>
              <option value="America/New_York">America/New_York</option>
              <option value="UTC">UTC</option>
            </select>
            <div className="mt-3 space-y-2">
              {BASE_AVAILABILITY.map((day) => (
                <div key={day.id}>
                  <p className="text-xs font-semibold text-[#075E54]">{day.label}</p>
                  {day.slots.map((slot) => {
                    const id = slotId(day.id, slot);
                    return (
                      <label key={id} className="flex items-center gap-2 py-0.5 text-sm text-[#122118]">
                        <input
                          type="checkbox"
                          checked={schedule.slotIds.includes(id)}
                          onChange={() =>
                            setSchedule((current) => ({
                              ...current,
                              slotIds: current.slotIds.includes(id)
                                ? current.slotIds.filter((item) => item !== id)
                                : [...current.slotIds, id],
                            }))
                          }
                        />
                        {slot}
                      </label>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <input type="date" className={inputClass} value={exception.date} onChange={(event) => setException({ ...exception, date: event.target.value })} aria-label="Jour indisponible" />
              <input className={inputClass} placeholder="Note" value={exception.note} onChange={(event) => setException({ ...exception, note: event.target.value })} />
              <button
                type="button"
                className="rounded-full border border-gray-200 px-3 text-xs font-semibold"
                onClick={() => {
                  if (!exception.date) return;
                  setSchedule((current) => ({
                    ...current,
                    exceptions: [...current.exceptions, { id: `ex-${Date.now()}`, date: exception.date, note: exception.note }],
                  }));
                  setException({ date: "", note: "" });
                }}
              >
                Ajouter
              </button>
            </div>
            <ul className="mt-2 space-y-1 text-sm text-[#4a5f53]">
              {schedule.exceptions.map((item) => (
                <li key={item.id}>
                  {item.date} {item.note}
                  <button
                    type="button"
                    className="ml-2 font-semibold text-[#122118]"
                    onClick={() =>
                      setSchedule((current) => ({
                        ...current,
                        exceptions: current.exceptions.filter((row) => row.id !== item.id),
                      }))
                    }
                  >
                    Retirer
                  </button>
                </li>
              ))}
            </ul>
            <label className="mt-5 block text-xs font-bold uppercase tracking-wider text-[#189346]" htmlFor="schedule-daily">
              Planification quotidienne
            </label>
            <p className="mt-1 text-xs text-[#4a5f53]">Heure du suivi de chaque tâche.</p>
            <input
              id="schedule-daily"
              data-testid="schedule-daily"
              type="time"
              className={`${inputClass} mt-2`}
              value={schedule.dailyPlan}
              onChange={(event) => setSchedule({ ...schedule, dailyPlan: event.target.value })}
            />
            <p className="mt-4 text-xs font-bold uppercase tracking-wider text-[#189346]">Planification de la semaine</p>
            <p className="mt-1 text-xs text-[#4a5f53]">Le jeudi, avant le début de la fin de semaine.</p>
            <div className="mt-2 flex gap-2">
              <select
                aria-label="Jour de la planification"
                data-testid="schedule-weekly-day"
                className={inputClass}
                value={schedule.weeklyPlanDay}
                onChange={(event) => setSchedule({ ...schedule, weeklyPlanDay: event.target.value })}
              >
                {WEEK_DAYS.map(([id, label]) => (
                  <option key={id} value={id}>{label}</option>
                ))}
              </select>
              <input
                aria-label="Heure de la planification"
                data-testid="schedule-weekly-time"
                type="time"
                className={inputClass}
                value={schedule.weeklyPlanTime}
                onChange={(event) => setSchedule({ ...schedule, weeklyPlanTime: event.target.value })}
              />
            </div>
            <p className="mt-4 text-xs font-bold uppercase tracking-wider text-[#189346]">Rendez-vous spécifique</p>
            <div className="mt-2 flex gap-2">
              <input
                type="date"
                aria-label="Date du rendez-vous"
                data-testid="schedule-appointment-date"
                className={inputClass}
                value={appointment.date}
                onChange={(event) => setAppointment({ ...appointment, date: event.target.value })}
              />
              <input
                type="time"
                aria-label="Heure du rendez-vous"
                data-testid="schedule-appointment-time"
                className={inputClass}
                value={appointment.time}
                onChange={(event) => setAppointment({ ...appointment, time: event.target.value })}
              />
            </div>
            <div className="mt-2 flex gap-2">
              <input
                className={inputClass}
                placeholder="Note"
                aria-label="Note du rendez-vous"
                data-testid="schedule-appointment-note"
                value={appointment.note}
                onChange={(event) => setAppointment({ ...appointment, note: event.target.value })}
              />
              <button
                type="button"
                data-testid="schedule-appointment-add"
                className="rounded-full border border-gray-200 px-3 text-xs font-semibold"
                onClick={() => {
                  if (!appointment.date) return;
                  setSchedule((current) => ({
                    ...current,
                    appointments: [
                      ...current.appointments,
                      { id: `rdv-${Date.now()}`, date: appointment.date, time: appointment.time, note: appointment.note },
                    ],
                  }));
                  setAppointment({ date: "", time: "", note: "" });
                }}
              >
                Ajouter
              </button>
            </div>
            <ul className="mt-2 space-y-1 text-sm text-[#4a5f53]">
              {schedule.appointments.map((item) => (
                <li key={item.id} data-testid={`appointment-${item.id}`}>
                  {item.date} {item.time} {item.note}
                  <button
                    type="button"
                    className="ml-2 font-semibold text-[#122118]"
                    onClick={() =>
                      setSchedule((current) => ({
                        ...current,
                        appointments: current.appointments.filter((row) => row.id !== item.id),
                      }))
                    }
                  >
                    Retirer
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              data-testid="schedule-save"
              className="mt-3 rounded-full px-4 py-2 text-sm font-semibold"
              style={{ background: "var(--sp-primary)", color: "var(--sp-text-on-primary)" }}
              onClick={() =>
                run(() =>
                  api("/api/family/schedule", session.token, {
                    method: "PUT",
                    body: JSON.stringify(schedule),
                  })
                )
              }
            >
              Enregistrer l'horaire
            </button>
          </div>
        )}

        {parent && demoEnabled && (
          <button
            type="button"
            data-testid="demo-reset"
            className="mt-8 text-sm font-semibold text-[#4a5f53] underline"
            onClick={() =>
              run(async () => {
                await api("/api/dev/reset", session.token, { method: "POST" });
                setNotice("Comptes de démonstration effacés.");
              })
            }
          >
            Effacer la démo locale
          </button>
        )}
      </div>
    </div>
  );
};

export default FamilySettings;
